import {createReducer} from "redux-act";
import {
	changeAvatar,
	changeAvatarButtonType,
	changeAvatarFailed,
	changeAvatarSuccess,
	changePassword,
	changePasswordFailed,
	changePasswordSuccess,
	clearChangePasswordError,
	clearUserError,
	clearUserState,
	forgotPassword,
	forgotPasswordFailed,
	forgotPasswordSuccess,
	login,
	logout,
	navigateTo,
	recoveryPassword,
	recoveryPasswordFailed,
	recoveryPasswordSuccess,
	register,
	requestUser,
	requestUserFailed,
	requestUserSuccess,
	setChangePasswordError,
	setRedirectUri,
	setUserError,
	updateUser,
	updateUserFailed,
	updateUserSuccess,
	userLoginFailed,
	userLoginSuccess,
	userLogoutFailed,
	userLogoutSuccess,
	userRegisterFailed,
	userRegisterSuccess,
	validateToken,
	validateTokenFailed,
	validateTokenSuccess,
} from "modules/actions";
import {ButtonType, RequestState} from "modules/enums";
import {IFormError} from "modules/types";
import {omit} from "lodash";

export interface IUser {
	id: number;
	email: string;
	password: string;
	firstName: string;
	lastName: string;
	favoriteTeam: number;
	username: string;
	lang: string;
	country: string;
	locationCode: string;
	isNotificationsEnabled: boolean;
	termsGZ: boolean;
	optInGZ: boolean;
	avatar: string | null;
}

export interface IUserReducer {
	user: IUser | null;
	requestState: RequestState;
	sessionCheckedState: RequestState;
	tokenRequestState: RequestState;
	changePasswordRequestState: RequestState;
	changeAvatarRequestState: RequestState;
	changeAvatarButtonType: ButtonType;
	logoutRequestState: RequestState;
	errors: IFormError;
	changePasswordErrors: IFormError;
	redirectUri: string;
	navigateToUri: string;
}

const initialState: IUserReducer = {
	user: null,
	requestState: RequestState.IDLE,
	sessionCheckedState: RequestState.IDLE,
	tokenRequestState: RequestState.IDLE,
	changePasswordRequestState: RequestState.IDLE,
	changeAvatarRequestState: RequestState.IDLE,
	changeAvatarButtonType: ButtonType.Enabled,
	logoutRequestState: RequestState.IDLE,
	errors: {},
	changePasswordErrors: {},
	redirectUri: "",
	navigateToUri: "",
};

const onRequestUser = (state: IUserReducer) => ({
	...state,
	sessionCheckedState: RequestState.Requested,
});

const onRequestUserSuccess = (state: IUserReducer, user: IUser) => ({
	...state,
	user,
	sessionCheckedState: RequestState.Received,
});

const onRequestUserFailed = (state: IUserReducer) => ({
	...state,
	sessionCheckedState: RequestState.Received,
});

const onUserRegister = (state: IUserReducer) => ({
	...state,
	requestState: RequestState.Requested,
});

// Used few times
const onUserRegisterSuccess = (state: IUserReducer, user: IUser) => ({
	...state,
	user,
	requestState: RequestState.Received,
});
// Used few times
const onUserAuthFailed = (state: IUserReducer, errors: IFormError) => ({
	...state,
	requestState: RequestState.Received,
	errors,
});

const onChangePassword = (state: IUserReducer) => ({
	...state,
	changePasswordRequestState: RequestState.Requested,
});

const onChangePasswordSuccess = (state: IUserReducer) => ({
	...state,
	changePasswordRequestState: RequestState.Received,
});

const onChangePasswordFailed = (state: IUserReducer, payload: IFormError) => ({
	...state,
	changePasswordRequestState: RequestState.Received,
	changePasswordErrors: payload,
});

const onChangeAvatar = (state: IUserReducer) => ({
	...state,
	changeAvatarRequestState: RequestState.Requested,
});

const onChangeAvatarSuccess = (state: IUserReducer, payload: IUser) => ({
	...state,
	changeAvatarRequestState: RequestState.Received,
	user: payload,
});

const onChangeAvatarFailed = (state: IUserReducer) => ({
	...state,
	changeAvatarRequestState: RequestState.Received,
});

const onChangeAvatarButtonType = (state: IUserReducer, payload: ButtonType) => ({
	...state,
	changeAvatarButtonType: payload,
});

const onUserLogout = (state: IUserReducer) => ({
	...state,
	logoutRequestState: RequestState.Requested,
});

const onUserLogoutSuccess = (state: IUserReducer) => ({
	...state,
	user: null,
	logoutRequestState: RequestState.Received,
});

const onUserLogoutFailed = (state: IUserReducer) => ({
	...state,
	logoutRequestState: RequestState.Received,
});

const onForgotPasswordSuccess = (state: IUserReducer) => ({
	...state,
	requestState: RequestState.Received,
});

const onRecoveryPasswordSuccess = (state: IUserReducer) => ({
	...state,
	requestState: RequestState.Received,
});

const onValidateToken = (state: IUserReducer) => ({
	...state,
	tokenRequestState: RequestState.Requested,
});

const onValidateTokenSuccess = (state: IUserReducer) => ({
	...state,
	tokenRequestState: RequestState.Received,
});

const onValidateTokenError = (state: IUserReducer) => ({
	...state,
	tokenRequestState: RequestState.Received,
});

const onSetRedirectUri = (state: IUserReducer, payload: string) => ({
	...state,
	redirectUri: payload,
});

const onNavigateTo = (state: IUserReducer, payload: string) => ({
	...state,
	navigateToUri: payload,
});

const onSetUserError = (state: IUserReducer, errors: IFormError) => ({
	...state,
	errors,
});

const onSetChangePasswordError = (state: IUserReducer, payload: IFormError) => ({
	...state,
	changePasswordErrors: payload,
});

const onClearUserError = (state: IUserReducer, payload: string | void) => ({
	...state,
	errors: payload ? omit(state.errors, [payload]) : {},
});

const onClearChangePasswordError = (state: IUserReducer, payload: string | void) => ({
	...state,
	changePasswordErrors: payload ? omit(state.changePasswordErrors, [payload]) : {},
});

const onClearUserState = () => ({...initialState});

export const user = createReducer<IUserReducer>({}, initialState)
	/**
	 * getUser
	 * */
	.on(requestUser, onRequestUser)
	.on(requestUserSuccess, onRequestUserSuccess)
	.on(requestUserFailed, onRequestUserFailed)
	/**
	 * registration
	 * */
	.on(register, onUserRegister)
	.on(userRegisterSuccess, onUserRegisterSuccess)
	.on(userRegisterFailed, onUserAuthFailed)
	/**
	 * login
	 * */
	.on(login, onUserRegister)
	.on(userLoginSuccess, onUserRegisterSuccess)
	.on(userLoginFailed, onUserAuthFailed)
	/**
	 * forgotPassword
	 * */
	.on(forgotPassword, onUserRegister)
	.on(forgotPasswordSuccess, onForgotPasswordSuccess)
	.on(forgotPasswordFailed, onUserAuthFailed)
	/**
	 * recoveryPassword
	 * */
	.on(recoveryPassword, onUserRegister)
	.on(recoveryPasswordSuccess, onRecoveryPasswordSuccess)
	.on(recoveryPasswordFailed, onUserAuthFailed)
	/**
	 * validateToken
	 * */
	.on(validateToken, onValidateToken)
	.on(validateTokenSuccess, onValidateTokenSuccess)
	.on(validateTokenFailed, onValidateTokenError)
	/**
	 * userUpdate
	 * */
	.on(updateUser, onUserRegister)
	.on(updateUserSuccess, onUserRegisterSuccess)
	.on(updateUserFailed, onUserAuthFailed)
	/**
	 * changePassword
	 * */
	.on(changePassword, onChangePassword)
	.on(changePasswordSuccess, onChangePasswordSuccess)
	.on(changePasswordFailed, onChangePasswordFailed)
	/**
	 * changeAvatar
	 * */
	.on(changeAvatar, onChangeAvatar)
	.on(changeAvatarSuccess, onChangeAvatarSuccess)
	.on(changeAvatarFailed, onChangeAvatarFailed)
	.on(changeAvatarButtonType, onChangeAvatarButtonType)
	/**
	 * redirectUri
	 */
	.on(setRedirectUri, onSetRedirectUri)
	.on(navigateTo, onNavigateTo)
	/**
	 * logout
	 * */
	.on(logout, onUserLogout)
	.on(userLogoutFailed, onUserLogoutFailed)
	.on(userLogoutSuccess, onUserLogoutSuccess)
	/**
	 * Error
	 */
	.on(setUserError, onSetUserError)
	.on(setChangePasswordError, onSetChangePasswordError)
	.on(clearUserError, onClearUserError)
	.on(clearChangePasswordError, onClearChangePasswordError)
	/**
	 * clear
	 * */
	.on(clearUserState, onClearUserState);
